import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import {
  MDBContainer,
  MDBRow,
  MDBBtn,
  MDBCol,
  MDBIcon,
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBAlert,
  MDBModalDialog,
  MDBModalContent,
  MDBModalTitle,
} from 'mdb-react-ui-kit'
import CustomAlert from '../../shared/CustomAlert/CustomAlert.component'
import OnboardingStore from './../Onboarding.store'
import UserProfileService from './../../../shared/services/UserProfile.service'
import { isWhiteLabel } from './../../../shared/utilities/whiteLabel.function'
import ContentHtml from './../../content/ContentHtml/ContentHtml.component'
import ACMStore from './../../contracting/AgentContractManager.store'
import UserContractCartItemFactory from './../../../shared/factories/user-contract-cart-item.factory'
import './OnboardingStageManager.scss'
import { toast } from 'react-toastify'

const OnboardingStageManager = ({ validity }) => {
  const { upline_fname, upline_lname, upline_email, d_name } = UserProfileService.getUserDetails(),
    canAdvance = OnboardingStore.canAdvance(),
    [showModal, setShowModal] = useState(false),
    [allCartItems, setAllCartItems] = useState([])

  const history = useHistory()

  // Fetch ALL cart items directly from database to avoid filtered items
  useEffect(() => {
    const fetchAllCartItems = async () => {
      if (!ACMStore?.Cart?.id()) {
        setAllCartItems([])
        return
      }

      try {
        const cartId = ACMStore.Cart.id()
        const items = await UserContractCartItemFactory.findAllByCartId(cartId)
        setAllCartItems(items || [])
      } catch (error) {
        console.error('[OnboardingStageManager] Error fetching cart items:', error)
        setAllCartItems([])
      }
    }

    fetchAllCartItems()
  }, [ACMStore?.Cart?.id(), ACMStore.cartItemsVersion])

  const canComplete = () => {
    if (`${UserProfileService.get('has_onboarded')}` !== '1') {
      const validity = toJS(OnboardingStore.validity)
      const currentStageNames = OnboardingStore.getStages().map(s => s.name)
      const stages = OnboardingStore.getStages()
      const finalStage = stages[stages.length - 1]
      if (validity[finalStage.name] === true) return true

      return currentStageNames.every(name => validity && validity[name] === true)
    }

    return true
  }

  const completeOnboarding = async () => {
    try {
      await OnboardingStore.complete()
      UserProfileService.set('has_onboarded', 1)

      if (isWhiteLabel()) {
        history.push('/agent-contracting')
        return
      }

      const CONVERTS_TO_COMPLETE = [91, 149, 37]
      if (CONVERTS_TO_COMPLETE.includes(parseInt(UserProfileService.get('usertype_id'))))
        UserProfileService.set('usertype_id', 92)

      setShowModal(true)
    } catch (err) {
      toast.error(err.message)
    }
  }

  const renderStageNextIcon = () => (
    <li className='stage-next'>
      <div className='ind-bg'>
        <MDBIcon icon='arrow-right' />
      </div>
    </li>
  )

  const renderStageIndicator = stage => (
    <li
      className={
        'stage-indicator ' +
        [
          OnboardingStore.validity[stage?.name] ? 'is-valid' : 'is-invalid',
          OnboardingStore.activeStage.name === stage?.name ? 'is-active' : 'is-inactive',
        ].join(' ')
      }
      onClick={() => {
        if (OnboardingStore.validity[stage?.name] || OnboardingStore.canGoTo(stage))
          OnboardingStore.setActiveStage(stage)
      }}
    >
      <div
        className={
          'ind-bg ' +
          (OnboardingStore.activeStage.name === stage?.name
            ? 'alert-primary'
            : OnboardingStore.validity[stage?.name]
            ? 'alert-success'
            : 'alert-danger')
        }
      >
        <span className='stage-name'>
          {stage?.index}. {stage?.label}
        </span>
      </div>
      <div
        className={
          'stage-validity ' +
          (OnboardingStore.activeStage.name === stage?.name
            ? 'alert-primary'
            : OnboardingStore.validity[stage?.name]
            ? 'alert-success'
            : 'alert-danger')
        }
      >
        {OnboardingStore.activeStage.name === stage?.name
          ? 'current'
          : OnboardingStore.validity[stage?.name]
          ? 'complete'
          : 'incomplete'}
      </div>
      <div className='description'>{stage?.description}</div>
    </li>
  )

  const getIncompletePackets = () => {
    // Get list of packet IDs (item_groups) that have items with additional_link in EXECUTED state
    // Use allCartItems from database to avoid filtered items
    const cartItems = allCartItems.length > 0 ? allCartItems : ACMStore?.CartItems || []
    if (!cartItems || cartItems.length === 0) return []

    const incompletePackets = new Set()

    cartItems.forEach(CartItem => {
      const hasAdditionalLink =
        CartItem.get('additional_link') != null &&
        CartItem.get('additional_link') !== '' &&
        String(CartItem.get('additional_link')).trim().length > 0
      const isBothLinks = parseInt(CartItem.get('is_both_links')) === 1
      const itemStatus = CartItem.get('item_status')
      const itemGroup = CartItem.get('item_group')

      // Item has additional links and is in EXECUTED state (needs manual completion)
      // Or item has additional links and is not COMPLETED
      if (itemGroup && (hasAdditionalLink || isBothLinks) && itemStatus !== 'COMPLETED') {
        incompletePackets.add(itemGroup)
      }
    })

    return Array.from(incompletePackets).sort()
  }

  const renderStageIndicators = () => {
    let lis = []
    OnboardingStore.getStages().forEach((stage, idx) => {
      lis.push(renderStageIndicator(stage))
      if (idx + 1 < OnboardingStore.getStages().length) lis.push(renderStageNextIcon(stage))
    })

    return lis.map((li, idx) => <React.Fragment key={'stage-indicator-idx-' + idx}>{li}</React.Fragment>)
  }

  const renderContractingAlert = () => {
    // Only show alert if we're on contracting stage or it's incomplete with additional links pending
    const isContractingStage = OnboardingStore.activeStage?.name === 'contracting'
    const contractingIncomplete = !OnboardingStore.validity.contracting
    const incompletePackets = getIncompletePackets()

    if ((isContractingStage || contractingIncomplete) && incompletePackets.length > 0) {
      const handlePacketClick = packetId => {
        // Navigate to contracting stage and trigger packet view
        if (OnboardingStore.activeStage?.name !== 'contracting') {
          OnboardingStore.setActiveStage(OnboardingStore.getStages().find(s => s.name === 'contracting'))
        }
        // Store the packet ID to view when component loads
        setTimeout(() => {
          ACMStore.viewPacket(packetId).catch(err => {
            console.error('Error viewing packet:', err)
            toast.error('Unable to open packet. Please try again.')
          })
        }, 100)
      }

      return (
        <CustomAlert color='warning' className='contracting-incomplete-alert'>
          <MDBIcon icon='exclamation-triangle' className='mr-2' />
          <div>
            <strong>Action Required:</strong> You have contract packets with additional links that require manual
            completion:{' '}
            {incompletePackets.map((packetId, idx) => (
              <React.Fragment key={`packet-link-${packetId}`}>
                <strong
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => handlePacketClick(packetId)}
                  title={`Click to view Packet ${packetId.toUpperCase()}`}
                >
                  Packet {packetId.toUpperCase()}
                </strong>
                {idx < incompletePackets.length - 1 ? ', ' : ''}
              </React.Fragment>
            ))}
            .
            <br />
            <br />
            Please open each packet and click the "Click Here Once Complete" button after finishing all contracts.
          </div>
        </CustomAlert>
      )
    }
    return null
  }

  // const renderNextSteps = () =>
  //   canAdvance ? (
  //     <div className="can-advance">
  //       You've completed the {OnboardingStore.activeStage.label}.<br />
  //       You can now start {OnboardingStore.getNext(true)?.label}. Click Start
  //       Next Stage.
  //     </div>
  //   ) : (
  //     <div className="cant-advance">
  //       Complete the form below to finish{' '}
  //       {OnboardingStore.getReadableName(OnboardingStore.activeStage.name)}.
  //     </div>
  //   )

  const renderUpline = () => (
    <table className='current-upline-table'>
      <thead>
        <tr>
          <th colSpan='2'>Got Questions? Need Guidance? Reach Your Upline Agent Here!</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>My Upline Agent:</td>
          <td>
            {[upline_fname, upline_lname].join(' ').trim()}, <a href={'mailto:' + upline_email}>{upline_email}</a>
          </td>
        </tr>
        <tr>
          <td>My Division: </td>
          <td>{d_name}</td>
        </tr>
      </tbody>
    </table>
  )

  const renderButtons = () => {
    const isFirst = parseInt(OnboardingStore.activeStage.index) === 1,
      isLast = parseInt(OnboardingStore.activeStage.index) === OnboardingStore.getStages().length,
      onlyOne = toJS(OnboardingStore.getStages()).length === 1

    if (onlyOne) {
      return (
        <>
          {canComplete() ? (
            <MDBBtn
              type='button'
              color='default'
              block
              className='btn-block btn-short can-advance'
              onClick={completeOnboarding}
            >
              Complete {isWhiteLabel() ? 'Onboarding' : 'USABG Onboarding'}
            </MDBBtn>
          ) : (
            <MDBBtn type='button' color='default' block className='btn-block btn-short cant-advance' disabled={true}>
              Complete {OnboardingStore.getReadableName(OnboardingStore.activeStage)} below to advance.
            </MDBBtn>
          )}
        </>
      )
    } else if (isFirst && !isLast)
      return (
        <>
          {canAdvance ? (
            <MDBBtn
              type='button'
              color='default'
              block
              className='btn-block can-advance'
              onClick={() => OnboardingStore.goTo(OnboardingStore.getNext(true))}
            >
              Start Next Stage&nbsp;
              <MDBIcon icon='arrow-right' />
            </MDBBtn>
          ) : (
            <MDBBtn type='button' color='default' block className='btn-block cant-advance' disabled={true}>
              Complete {OnboardingStore.getReadableName(OnboardingStore.activeStage)} below to advance.
            </MDBBtn>
          )}
        </>
      )
    else if (!isFirst && isLast)
      return (
        <>
          {canComplete() ? (
            <MDBBtn
              type='button'
              color='default'
              block
              className='btn-block btn-short can-advance'
              onClick={completeOnboarding}
            >
              Complete {isWhiteLabel() ? 'Onboarding' : 'USABG Onboarding'}
            </MDBBtn>
          ) : (
            <MDBBtn type='button' color='default' block className='btn-block btn-short' disabled={true}>
              You're almost done
            </MDBBtn>
          )}

          <MDBBtn
            type='button'
            color='default'
            block
            className='btn-block btn-short go-back btn-default'
            disabled={!OnboardingStore.canRewind()}
            onClick={() => OnboardingStore.goTo(OnboardingStore.getPrev(true))}
          >
            Go Back To {OnboardingStore.getReadableName(OnboardingStore.getPrev(true))}
          </MDBBtn>
        </>
      )

    return (
      <>
        {canAdvance ? (
          <MDBBtn
            type='button'
            color='default'
            block
            className='btn-block can-advance'
            onClick={() => OnboardingStore.goTo(OnboardingStore.getNext(true))}
          >
            Start Next Stage&nbsp;
            <MDBIcon icon='arrow-right' />
          </MDBBtn>
        ) : (
          <MDBBtn type='button' color='default' block className='btn-block btn-short cant-advance' disabled={true}>
            Complete {OnboardingStore.getReadableName(OnboardingStore.activeStage)} below to advance.
          </MDBBtn>
        )}
        <MDBBtn
          type='button'
          color='default'
          block
          className='btn-block btn-short go-back btn-default'
          disabled={!OnboardingStore.canRewind()}
          onClick={() => OnboardingStore.goTo(OnboardingStore.getPrev(true))}
        >
          Go Back To {OnboardingStore.getReadableName(OnboardingStore.getPrev(true))}
        </MDBBtn>
      </>
    )
  }

  return (
    <MDBContainer id='OnboardingStageManagerComponent'>
      <MDBRow>
        <MDBCol size='12'>
          <ul>{renderStageIndicators()}</ul>
        </MDBCol>
      </MDBRow>
      <MDBRow>
        <MDBCol size='12'>{renderContractingAlert()}</MDBCol>
      </MDBRow>
      <MDBRow>
        <MDBCol size='12' md='5' lg='6'>
          {renderUpline()}
        </MDBCol>
        <MDBCol size='12' md='7' lg='6'>
          {renderButtons()}
        </MDBCol>
      </MDBRow>
      <MDBModal open={showModal} onClose={() => setShowModal(false)}>
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>You Did It!</MDBModalTitle>
              <MDBBtn type='button' className='btn-close' color='none' onClick={() => setShowModal(false)}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              {UserProfileService.isA(['career-agent']) ? (
                <ContentHtml contentSlug='career-onboarding-complete' />
              ) : (
                <ContentHtml contentId='99' />
              )}
              <MDBBtn type='button' color='indigo' block onClick={() => history.push('/dashboard')}>
                Take me to the Back Office homepage.
              </MDBBtn>
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </MDBContainer>
  )
}

export default observer(OnboardingStageManager)
